<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('products.fulfillmentGroups.form.name')"
            :is-required="true"
            :error="errors.name"
            :hint="__('products.fulfillmentGroups.form.name_hint')"
        >
            <DInput
                id="name"
                v-model="form.name"
                :placeholder="__('products.fulfillmentGroups.form.name')"
                :is-valid="isFormFieldValid('name')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentGroups.form.fulfillmentModule')"
            :is-required="true"
            :error="errors.module"
            :hint="__('products.fulfillmentGroups.form.fulfillmentModule_hint')"
        >
            <DSelect
                id="fulfillment_module"
                v-model="form.module"
                :options="fulfillmentModuleOptions"
                :disabled="changeModule"
                :placeholder="__('products.fulfillmentGroups.form.fulfillmentModule')"
                :is-valid="isFormFieldValid('module')"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentGroups.form.tag')"
            :error="errors.tags"
            :hint="__('products.fulfillmentGroups.form.tag_hint')"
        >
            <DMultiSelect
                id="account_tags"
                :options="selectedAccountTagOptions()"
                :selection="true"
                selection-type="tags"
                :search="true"
                @update="updateSelectedAccountTags"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentGroups.form.data_template')"
            :hint="__('products.fulfillmentGroups.form.data_template_hint')"
        >
            <DFormTextarea
                id="additional_data"
                v-model="form.data_template"
                :placeholder="__('products.fulfillmentGroups.form.data_template')"
                :is-valid="isAdditionalDataValid()"
                class="mb-0"
            />
            <div
                v-if="isAdditionalDataValid() === false"
                class="invalid-feedback d-block"
            >
                <div
                    v-for="(additionalDataError, index) in additionalDataErrors"
                    :key="index"
                >
                    {{ additionalDataError }}
                </div>
            </div>
        </DFormRow>

        <template v-if="accountOptions.length">
            <h5 class="mt-4">
                {{ __('products.fulfillmentAccountOptions.section_title') }}
            </h5>
            <AccountOptionFields
                :options="accountOptions"
                :values="form.account_options"
                :errors="errors"
                :test-route="testRoute"
                tier="group"
            />
        </template>

        <DFormControls
            :cancel-route="$route('products.accounts.index')"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>
import _ from 'lodash'
import AccountOptionFields from '~@/Components/Fulfillment/AccountOptions/AccountOptionFields'

export default {
    name: 'FulfillmentAccountGroupForm',
    components: {
        AccountOptionFields
    },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        accountGroup: {
            type: Object,
            default: () => { return {} }
        },
        fulfillmentModules: {
            type: Array,
            default: () => { return [] }
        },
        accountTags: {
            type: Array,
            default: () => { return [] }
        },
        tags: {
            type: Array,
            default: () => { return [] }
        },
        changeModule: {
            type: Boolean,
            default: false
        },
        accountOptions: {
            type: Array,
            default: () => { return [] }
        },
        testRoute: {
            type: String,
            default: null
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                name: this.accountGroup?.name ?? null,
                module: this.accountGroup?.module ?? null,
                data_template: !_.isEmpty(this.accountGroup?.data_template) ? JSON.stringify(this.accountGroup?.data_template, null, 6) : null,
                additional_data_parsed: null,
                tags: Array.isArray(this.tags)
                    ? this.tags
                    : [],
                account_options: this.buildAccountOptionValues(),
            })
        }
    },
    computed: {
        fulfillmentModuleOptions () {
            return this.fulfillmentModules.map((module) => {
                return {
                    label: module,
                    value: module
                }
            })
        },
        additionalDataErrors () {
            const errorsList = []

            Object.keys(this.errors).forEach((key) => {
                if (key.startsWith('additional_data')) {
                    errorsList.push(this.errors[key])
                }
            })

            return errorsList
        }
    },
    methods: {
        buildAccountOptionValues () {
            const values = {}

            this.accountOptions.forEach((option) => {
                values[option.name] = option.encrypted ? '' : (option.value ?? '')
            })

            return values
        },
        submitForm () {
            if (this.form.data_template !== null && this.form.data_template?.length !== 0) {
                try {
                    this.form.additional_data_parsed = JSON.parse(this.form.data_template)
                } catch (e) {
                    this.form.additional_data_parsed = {}
                }
            }
            this.$emit('form-submitted', this.form)
        },
        isAdditionalDataValid () {
            const errorsKeys = Object.keys(this.errors)

            if (errorsKeys.length === 0) {
                return null
            }

            const additionalDataErrors = errorsKeys.filter((key) => {
                return key.startsWith('additional_data')
            })

            return additionalDataErrors.length === 0
        },
        updateSelectedAccountTags (values) {
            this.form.tags = values
        },
        selectedAccountTagOptions () {
            if (this.tags.length > 0) {
                const selectedTags = this.tags.map(tag => tag.value)
                return this.accountTags
                    .map((option) => ({ ...option, selected: selectedTags.includes(option.value) }))
            }

            return this.accountTags
        },
    }
}
</script>
